import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useGetRestaurantUsersApi } from './use-get-restaurant-users-api';

const endpoint = 'api/v1/restaurants/users';
export const createRestaurantUserEndpointIdentifier = endpoint;

interface CreateRestaurantUserBodyType {
  name: string;
  phoneNumber: string;
  email: string;
  roleId: number;
  subEntities: {
    id: number;
    type: 'branch';
  }[];
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  body: CreateRestaurantUserBodyType;
};
export type CreateRestaurantUserVariables = TVariables;

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useCreateRestaurantUserApi(restaurantId: string, options?: Options): ReturnType {
  const toast = useCustomToast();
  const fullEndpoint = `${endpoint}/${restaurantId}`;

  const { refetch: refetchRestaurantUsersApi } = useGetRestaurantUsersApi(restaurantId, { enabled: false });

  const mutationFn = async ({ body }: TVariables) => {
    try {
      const { data } = await http.post(fullEndpoint, body);

      // Refetch restaurant users
      refetchRestaurantUsersApi();

      toast({
        description: 'New user has been created successfully',
        status: 'success',
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { mutationKey: fullEndpoint, ...options });
}
